import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Pagination,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
  AlertTitle,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { CircularProgress } from "@mui/material";
import {
  History,
  CalendarToday,
  LocalHospital,
  ExpandMore,
  Science,
  Biotech,
  Assignment,
  Info,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { DESIGN_TOKENS } from "../../../design-system/tokens/colors";
import { apiClient } from "../../../services/api";
import { API_CONFIG } from "../../../config/api";
import { storageService } from "../../../services/storageService";

export interface ExamData {
  _key: string;
  _id: string;
  cattle_tag: string;
  farm_id: string;
  handling_date: string;
  handling_time: string;
  handling_details: {
    exam_type: string;
    exam_result?: string;
    plate_id?: string;
    collection_date?: string;
    exam_date?: string;
    attachments?: Array<{
      type: string;
      url: string;
    }> | string | { type?: string; url?: string };
    results?: {
      staphPrimary?: string;
      staphSecondary?: string;
      strepPrimary?: string;
      strepSecondary?: string;
      generalPrimary?: string;
      generalSecondary?: string;
      milkAspect?: string[];
      udderSigns?: string[];
      teatsAffected?: string[];
      observation?: string;
      result?: string;
      exam_result?: string;
      // Novos campos de tratamento
      situation?: string;
      mainTreatment?: string;
      supportiveTreatment?: string;
      recommendedAntibiotics?: string;
      treatmentPlan?: any;
      aines?: string[];
    };
    [key: string]: any;
  };
  notes?: string;
  cost?: number;
  created_at: string;
  updated_at: string;
}

export interface AnimalExamListProps {
  examData: {
    data?: ExamData[];
    exams?: ExamData[];
    resumo?: string;
    content_type?: string;
    url?: string;
  } | ExamData[];
}

const AnimalExamList: React.FC<AnimalExamListProps> = ({ examData }) => {
  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Estado do modal de imagem
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalSrc, setImageModalSrc] = useState<string | null>(null);
  const [tempAccessLoadingKey, setTempAccessLoadingKey] = useState<string | null>(null);
  const [tempAccessError, setTempAccessError] = useState<string | null>(null);
  const [tempAccessErrorKey, setTempAccessErrorKey] = useState<string | null>(null);

  const handleOpenImageModal = (url: string) => {
    setImageModalSrc(url);
    setImageModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setImageModalOpen(false);
    setImageModalSrc(null);
  };

  const fetchTempAccessAndOpen = async (objectKey: string) => {
    try {
      setTempAccessLoadingKey(objectKey);
      setTempAccessError(null);
      setTempAccessErrorKey(null);
      const resp = await storageService.getTempAccess({
        bucket: (import.meta as any)?.env?.VITE_S3_BUCKET_EXAMES || "exames",
        objectKey,
        request: {
          expiresInMinutes: 300,
          maxAccess: 100,
          description: "Preview",
        },
      });
      if (resp?.accessUrl) {
        handleOpenImageModal(resp.accessUrl);
      } else {
        setTempAccessError("Não foi possível obter acesso temporário para a imagem.");
        setTempAccessErrorKey(objectKey);
      }
    } catch (e) {
      setTempAccessError("Erro ao gerar acesso temporário. Tente novamente.");
      setTempAccessErrorKey(objectKey);
    } finally {
      setTempAccessLoadingKey(null);
    }
  };

  

  // Extrair array de exames e informações do animal
  const { examsArray, animalInfo, resumo } = useMemo(() => {
    if (!examData) return { examsArray: [], animalInfo: null, resumo: '' };
    
    let exams: ExamData[] = [];
    let summary = '';
    
    // Se examData é um array, retorna diretamente
    if (Array.isArray(examData)) {
      exams = examData;
    } else if (examData && typeof examData === 'object') {
      // Se examData é um objeto, verifica se tem 'data' ou 'exams'
      exams = examData.data || examData.exams || [];
      summary = examData.resumo || '';
    }
    
    // Extrair informações do animal do primeiro exame
    const animalData = exams.length > 0 ? {
      cattle_tag: exams[0].cattle_tag,
      farm_id: exams[0].farm_id,
      totalExams: exams.length,
    } : null;
    
    
    
    return { 
      examsArray: exams, 
      animalInfo: animalData, 
      resumo: summary 
    };
  }, [examData]);

  // Calcular paginação
  const totalPages = Math.ceil(examsArray.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = examsArray.slice(startIndex, startIndex + itemsPerPage);

  // Função para obter cor baseada no tipo de exame
  const getExamColor = (examType: string) => {
    switch (examType) {
      case 'mastitis_test':
        return DESIGN_TOKENS.colors.primary[500];
      case 'blood_test':
        return DESIGN_TOKENS.colors.status.error[500];
      case 'urine_test':
        return DESIGN_TOKENS.colors.status.warning[500];
      default:
        return DESIGN_TOKENS.colors.neutral[500];
    }
  };

  // Função para obter ícone baseado no tipo de exame
  const getExamIcon = (examType: string) => {
    switch (examType) {
      case 'mastitis_test':
        return <LocalHospital />;
      case 'blood_test':
        return <Biotech />;
      case 'urine_test':
        return <Science />;
      default:
        return <Assignment />;
    }
  };

  // Função para formatar data
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Função para obter status do resultado
  const getResultStatus = (result: string | undefined) => {
    if (!result) return { color: 'default', label: 'Não informado' };
    
    const lowerResult = result.toLowerCase();
    if (lowerResult.includes('positivo')) {
      return { color: 'error', label: 'Positivo' };
    } else if (lowerResult.includes('negativo')) {
      return { color: 'success', label: 'Negativo' };
    }
    return { color: 'default', label: result };
  };

  // Helper: normalize attachments that can arrive as string, array, or object
  const getFirstImageAttachmentUrl = (attachments: any): string | null => {
    try {
      let arr: any[] = [];
      if (Array.isArray(attachments)) {
        arr = attachments;
      } else if (typeof attachments === 'string') {
        const parsed = JSON.parse(attachments);
        if (Array.isArray(parsed)) arr = parsed;
        else if (parsed && typeof parsed === 'object') arr = [parsed];
      } else if (attachments && typeof attachments === 'object') {
        arr = [attachments];
      }
      const att = arr.find((a) => a && a.type === 'image' && a.url);
      return att?.url ? String(att.url).replace(/[`]/g, '').trim() : null;
    } catch {
      return null;
    }
  };

  // Component: authenticated image loader (handles secure-access endpoints)
  const ExamImage: React.FC<{ src: string; alt?: string }> = ({ src, alt }) => {
    const [resolvedSrc, setResolvedSrc] = useState<string>(src);
    const [errored, setErrored] = useState<boolean>(false);

    useEffect(() => {
      let objectUrl: string | null = null;
      let cancelled = false;

      const load = async () => {
        try {
          const base = API_CONFIG.baseURL || "";
          let endpoint: string | null = null;
          if (base && src.startsWith(base)) {
            endpoint = src.substring(base.length);
          } else if (src.startsWith("/")) {
            endpoint = src; // relative to API base
          }

          if (endpoint) {
            const blob = await apiClient.getBlob(endpoint);
            if (cancelled) return;
            objectUrl = URL.createObjectURL(blob);
            setResolvedSrc(objectUrl);
          } else {
            setResolvedSrc(src);
          }
        } catch (e) {
          setErrored(true);
        }
      };

      load();
      return () => {
        cancelled = true;
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
      };
    }, [src]);

    if (errored) {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Não foi possível carregar a imagem.
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        component="img"
        src={resolvedSrc}
        alt={alt || "Imagem do exame"}
        sx={{
          width: "100%",
          maxWidth: 360,
          height: "auto",
          borderRadius: 1,
          border: "1px solid",
          borderColor: DESIGN_TOKENS.colors.neutral[200],
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
        onError={() => setErrored(true)}
      />
    );
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Modal para visualizar a imagem do exame */}
      <Dialog
        open={imageModalOpen}
        onClose={handleCloseImageModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { p: 2 }
        }}
      >
        <DialogTitle>Placa de teste</DialogTitle>
        <DialogContent>
          {imageModalSrc ? (
            <ExamImage src={imageModalSrc} alt="Placa de teste" />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Nenhuma imagem disponível.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
      {/* Seção de Informações do Animal */}
      {animalInfo && (
        <Card 
          sx={{ 
            mb: 3, 
            background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primary[50]} 0%, ${DESIGN_TOKENS.colors.primary[100]} 100%)`,
            border: `1px solid ${DESIGN_TOKENS.colors.primary[200]}`
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar
                sx={{ 
                  bgcolor: DESIGN_TOKENS.colors.primary[500],
                  width: 56,
                  height: 56,
                  mr: 2
                }}
              >
                <img 
                  src="/vaca.svg" 
                  alt="Vaca" 
                  style={{ 
                    width: 28, 
                    height: 28,
                    filter: 'brightness(0) invert(1)' // Torna a imagem branca
                  }} 
                />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.primary[700] }}>
                  Animal {animalInfo.cattle_tag}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Fazenda: {animalInfo.farm_id}
                </Typography>
              </Box>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
              <Box sx={{ textAlign: 'center', minWidth: 120 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.primary[600] }}>
                  {animalInfo.totalExams}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total de Exames
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <Info sx={{ mr: 1, color: DESIGN_TOKENS.colors.primary[500] }} />
                <Typography variant="body2" color="text.secondary">
                  {resumo || `${animalInfo.totalExams} exame(s) encontrado(s) para o animal ${animalInfo.cattle_tag}`}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Lista de Exames */}
      <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <History sx={{ mr: 2, color: DESIGN_TOKENS.colors.primary[500] }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Histórico de Exames
            </Typography>
          </Box>

          {examsArray.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <img 
                src="/gestores/Laboratório.svg" 
                alt="Laboratório" 
                style={{ 
                  width: 64, 
                  height: 64, 
                  opacity: 0.6, 
                  marginBottom: 16 
                }} 
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nenhum exame encontrado
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Não há exames registrados para este animal
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ position: 'relative' }}>
                {currentItems.map((exam, index) => (
                  <Box key={`${exam._key}-${index}`} sx={{ display: 'flex', mb: 3, position: 'relative' }}>
                    {/* Timeline Dot */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 3, minWidth: 40 }}>
                      <Avatar
                        sx={{ 
                          bgcolor: getExamColor(exam.handling_details?.exam_type || ''),
                          width: 40,
                          height: 40,
                          fontSize: 20
                        }}
                      >
                        {getExamIcon(exam.handling_details?.exam_type || '')}
                      </Avatar>
                      {index < currentItems.length - 1 && (
                        <Box 
                          sx={{ 
                            width: 2, 
                            height: 60, 
                            bgcolor: 'grey.300',
                            mt: 1,
                          }} 
                        />
                      )}
                    </Box>
                    
                    {/* Timeline Content */}
                    <Box sx={{ flex: 1, pt: 0.5 }}>
                      <Paper 
                        elevation={1} 
                        sx={{ 
                          p: 3, 
                          bgcolor: 'background.paper',
                          border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                          borderRadius: 2,
                          '&:hover': {
                            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                            transform: 'translateY(-1px)',
                            transition: 'all 0.2s ease-in-out'
                          }
                        }}
                      >
                        {/* Header do Exame */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {exam.handling_details?.exam_type === 'mastitis_test' ? 'Teste de Mastite' : 
                               exam.handling_details?.exam_type === 'blood_test' ? 'Exame de Sangue' :
                               exam.handling_details?.exam_type === 'urine_test' ? 'Exame de Urina' : 
                               'Exame Laboratorial'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(exam.handling_date)} às {exam.handling_time}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {exam.handling_details?.plate_id && (
                              <Chip 
                                label={`Placa: ${exam.handling_details.plate_id}`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                            {exam.handling_details?.results?.exam_result && (
                              <Chip 
                                label={getResultStatus(exam.handling_details.results.exam_result).label}
                                size="small"
                                color={getResultStatus(exam.handling_details.results.exam_result).color as any}
                              />
                            )}
                          </Box>
                        </Box>

                        {/* Imagem do exame (anexo) */}
                        {(() => {
                          const objectKey = getFirstImageAttachmentUrl(exam.handling_details?.attachments);
                          return objectKey ? (
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Button 
                                variant="outlined" 
                                size="small" 
                                startIcon={<Biotech />}
                                onClick={() => fetchTempAccessAndOpen(objectKey)}
                                disabled={tempAccessLoadingKey === objectKey}
                              >
                                Ver placa de teste
                              </Button>
                              {tempAccessLoadingKey === objectKey && (
                                <CircularProgress size={20} />
                              )}
                              {tempAccessError && tempAccessErrorKey === objectKey && (
                                <Typography variant="caption" color="error">
                                  {tempAccessError}
                                </Typography>
                              )}
                            </Box>
                          ) : null;
                        })()}

                        {/* Detalhes do Exame */}
                        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                          {exam.handling_details?.collection_date && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Data de Coleta
                              </Typography>
                              <Typography variant="body2">
                                {formatDate(exam.handling_details.collection_date)}
                              </Typography>
                            </Box>
                          )}
                          {exam.handling_details?.exam_date && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Data do Exame
                              </Typography>
                              <Typography variant="body2">
                                {formatDate(exam.handling_details.exam_date)}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Accordion para Resultados Detalhados */}
                        {exam.handling_details?.exam_type === 'mastitis_test' && exam.handling_details?.results && (
                          <>
                            {/* Dropdown 1: Resultados */}
                            <Accordion sx={{ mt: 2, boxShadow: 'none', border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}` }}>
                              <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  Resultado: {exam.handling_details?.results?.exam_result || 'Não informado'}
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  {/* Sinais do Úbere */}
                                  {exam.handling_details.results.udderSigns && exam.handling_details.results.udderSigns.length > 0 && (
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Sinais do Úbere:
                                      </Typography>
                                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {exam.handling_details.results.udderSigns.map((sign, idx) => (
                                          <Chip key={idx} label={sign} size="small" variant="outlined" />
                                        ))}
                                      </Box>
                                    </Box>
                                  )}

                                  {/* Tetos Afetados */}
                                  {exam.handling_details.results.teatsAffected && exam.handling_details.results.teatsAffected.length > 0 && (
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Tetos Afetados:
                                      </Typography>
                                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {exam.handling_details.results.teatsAffected.map((teat, idx) => (
                                          <Chip key={idx} label={teat} size="small" variant="outlined" />
                                        ))}
                                      </Box>
                                    </Box>
                                  )}

                                  {/* Aspecto do Leite */}
                                  {exam.handling_details.results.milkAspect && exam.handling_details.results.milkAspect.length > 0 && (
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Aspecto do Leite:
                                      </Typography>
                                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {exam.handling_details.results.milkAspect.map((aspect, idx) => (
                                          <Chip key={idx} label={aspect} size="small" variant="outlined" />
                                        ))}
                                      </Box>
                                    </Box>
                                  )}

                                  {/* Resultados Microbiológicos */}
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                      Resultados Microbiológicos:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                      {exam.handling_details.results.staphPrimary && (
                                        <Typography variant="body2">
                                          <strong>Staphylococcus (Primário):</strong> {exam.handling_details.results.staphPrimary}
                                        </Typography>
                                      )}
                                      {exam.handling_details.results.strepPrimary && (
                                        <Typography variant="body2">
                                          <strong>Streptococcus (Primário):</strong> {exam.handling_details.results.strepPrimary}
                                        </Typography>
                                      )}
                                      {exam.handling_details.results.generalPrimary && (
                                        <Typography variant="body2">
                                          <strong>Geral (Primário):</strong> {exam.handling_details.results.generalPrimary}
                                        </Typography>
                                      )}
                                      {exam.handling_details.results.generalSecondary && (
                                        <Typography variant="body2">
                                          <strong>Geral (Secundário):</strong> {exam.handling_details.results.generalSecondary}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                </Box>
                              </AccordionDetails>
                            </Accordion>

                            {/* Dropdown 2: Tratamento Recomendado */}
                            {(exam.handling_details.results.situation || exam.handling_details.results.mainTreatment || exam.handling_details.results.supportiveTreatment || exam.handling_details.results.recommendedAntibiotics || (exam.handling_details.results.aines && exam.handling_details.results.aines.length > 0)) && (
                              <Accordion 
                                sx={{
                                  mt: 1,
                                  bgcolor: 'grey.50',
                                  border: '1px solid',
                                  borderColor: 'grey.300',
                                  borderRadius: 1,
                                  '&:before': {
                                    display: 'none',
                                  },
                                  boxShadow: 'none',
                                }}
                              >
                                <AccordionSummary
                                  expandIcon={<ExpandMore sx={{ color: 'grey.600' }} />}
                                  sx={{
                                    bgcolor: 'grey.50',
                                    '& .MuiAccordionSummary-content': {
                                      alignItems: 'center',
                                    },
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                    <LocalHospital sx={{ color: 'primary.main', fontSize: 20 }} />
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                        Tratamento Recomendado
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Clique para ver recomendações de tratamento
                                      </Typography>
                                    </Box>
                                  </Box>
                                </AccordionSummary>
                                
                                <AccordionDetails sx={{ bgcolor: 'background.paper', pt: 2 }}>
                                  <Stack spacing={2} sx={{ mb: 3 }}>
                                    {exam.handling_details.results.situation && (
                                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 1 }}>
                                          Situação:
                                        </Box>
                                        <Box component="span" sx={{ color: 'text.secondary' }}>
                                          {exam.handling_details.results.situation}
                                        </Box>
                                      </Typography>
                                    )}
                                    {exam.handling_details.results.mainTreatment && (
                                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 1 }}>
                                          Tratamento principal:
                                        </Box>
                                        <Box component="span" sx={{ color: 'text.secondary' }}>
                                          {exam.handling_details.results.mainTreatment}
                                        </Box>
                                      </Typography>
                                    )}
                                    {exam.handling_details.results.supportiveTreatment && (
                                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 1 }}>
                                          Tratamento de suporte:
                                        </Box>
                                        <Box component="span" sx={{ color: 'text.secondary' }}>
                                          {exam.handling_details.results.supportiveTreatment}
                                        </Box>
                                      </Typography>
                                    )}
                                    {exam.handling_details.results.recommendedAntibiotics && (
                                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 1 }}>
                                          Antibióticos recomendados:
                                        </Box>
                                        <Box component="span" sx={{ color: 'text.secondary' }}>
                                          {exam.handling_details.results.recommendedAntibiotics}
                                        </Box>
                                      </Typography>
                                    )}
                                    {exam.handling_details.results.aines && exam.handling_details.results.aines.length > 0 && (
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                                          AINES:
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                          {exam.handling_details.results.aines.map((a, idx) => (
                                            <Chip key={`${a}-${idx}`} label={a} size="small" color="primary" variant="outlined" />
                                          ))}
                                        </Box>
                                      </Box>
                                    )}
                                  </Stack>

                                  {/* Aviso de IA movido para o final */}
                                  <Alert
                                    severity="warning"
                                    variant="outlined"
                                    icon={<WarningAmberOutlined />}
                                    role="alert"
                                    aria-live="polite"
                                    sx={{
                                      bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
                                      borderColor: 'warning.main',
                                      color: 'warning.dark',
                                      p: 2,
                                    }}
                                  >
                                    <AlertTitle>Atenção</AlertTitle>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      Esta é apenas uma recomendação gerada por IA, e ela pode errar. Consulte um veterinário.
                                    </Typography>
                                  </Alert>
                                </AccordionDetails>
                              </Accordion>
                            )}
                          </>
                        )}

                        {/* Notas */}
                        {exam.notes && (
                          <Box sx={{ mt: 2, p: 2, bgcolor: DESIGN_TOKENS.colors.neutral[50], borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Observações:
                            </Typography>
                            <Typography variant="body2">
                              {exam.notes}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Paginação */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_, page) => setCurrentPage(page)}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AnimalExamList;